import React, { useCallback, useRef, useEffect } from 'react';
import { Box, TextField, Select, MenuItem } from '@mui/material';
import type { EmployeeSpreadsheetCellField } from './types';
import { formatNumber } from '../../utils/formatters';

const cleanAmountInput = (input: string): number => {
  const v = input.replace(/[,₩\s]/g, '').replace(/[^\d]/g, '');
  return v ? Number(v) : 0;
};

const convertToDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value != null && 'toDate' in value) {
    const d = (value as { toDate: () => Date }).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (typeof value === 'string' && value) {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatDateYYMMDD = (date: Date): string => {
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}/${month}/${day}`;
};

interface EmployeeEditableCellProps {
  field: EmployeeSpreadsheetCellField;
  value: string | number | boolean | unknown;
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (value: string | number | boolean) => void;
  onCancel: () => void;
  align?: 'left' | 'right';
  severanceMessage?: string | null;
  readOnly?: boolean;
}

export const EmployeeEditableCell: React.FC<EmployeeEditableCellProps> = ({
  field,
  value,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  align = 'left',
  severanceMessage = null,
  readOnly = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (field === 'hire_date') {
        const el = inputRef.current as HTMLInputElement;
        requestAnimationFrame(() => el.showPicker?.());
      }
    }
  }, [isEditing, field]);

  const displayValue = (): string => {
    if (value === undefined || value === null) return '';
    if (field === 'hire_date') {
      const d = convertToDate(value);
      return d ? formatDateYYMMDD(d) : '미정';
    }
    if (field === 'isHourly') return value === true ? '시급제' : '월급제';
    if (field === 'hourlyRate' || field === 'monthlySalary') {
      const n = typeof value === 'number' ? value : 0;
      return n > 0 ? formatNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '';
    }
    if (field === 'payday') return value ? `${value}일` : '';
    if (field === 'dependents') return value != null ? `${value}명` : '0명';
    if (field === 'severance') return severanceMessage || '';
    if (field === 'isRetired') return value === true ? '퇴직' : '재직';
    return String(value);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, newValue?: string | number | boolean) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (newValue !== undefined) onSave(newValue);
        else onSave(String((e.target as HTMLInputElement).value));
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onSave, onCancel]
  );

  const canEdit = !readOnly && (field === 'name' || field === 'hire_date' || field === 'isHourly' || field === 'hourlyRate' || field === 'monthlySalary' || field === 'payday' || field === 'dependents');

  if (!canEdit) {
    return (
      <Box
        sx={{
          minHeight: 36,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          textAlign: align,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {displayValue() || ' '}
      </Box>
    );
  }

  if (!isEditing) {
    return (
      <Box
        onClick={onStartEdit}
        sx={{
          minHeight: 36,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '0 8px',
          '&:hover': { backgroundColor: 'action.hover' },
          textAlign: align,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {displayValue() || ' '}
      </Box>
    );
  }

  if (field === 'hire_date') {
    const d = convertToDate(value);
    const dateStr =
      d ? d.toISOString().slice(0, 10)
      : typeof value === 'string' && value.length >= 10
        ? value.slice(0, 10)
        : '';
    return (
      <TextField
        inputRef={inputRef}
        type="date"
        size="small"
        fullWidth
        variant="standard"
        value={dateStr}
        onChange={(e) => onSave(e.target.value)}
        onBlur={(e) => onSave(e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, (e.target as HTMLInputElement).value)}
        InputProps={{ disableUnderline: false }}
        sx={{ '& input': { fontSize: 13 }, minWidth: 0 }}
      />
    );
  }

  if (field === 'isHourly') {
    const isHourlyVal = value === true || value === 'true';
    return (
      <Select
        open
        autoFocus
        size="small"
        fullWidth
        variant="standard"
        value={isHourlyVal ? 'hourly' : 'salary'}
        onChange={(e) => onSave(e.target.value === 'hourly')}
        onClose={onCancel}
        onBlur={() => onSave(isHourlyVal)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(isHourlyVal);
          if (e.key === 'Escape') onCancel();
        }}
        sx={{ fontSize: 13, minHeight: 36 }}
      >
        <MenuItem value="hourly">시급제</MenuItem>
        <MenuItem value="salary">월급제</MenuItem>
      </Select>
    );
  }

  if (field === 'hourlyRate' || field === 'monthlySalary') {
    const numValue = typeof value === 'number' ? value : 0;
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        variant="standard"
        type="text"
        value={numValue > 0 ? numValue.toLocaleString('ko-KR') : ''}
        onChange={(e) => onSave(cleanAmountInput(e.target.value))}
        onBlur={(e) => onSave(cleanAmountInput(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(cleanAmountInput((e.target as HTMLInputElement).value));
          if (e.key === 'Escape') onCancel();
        }}
        InputProps={{ disableUnderline: false }}
        sx={{ '& input': { fontSize: 13, textAlign: 'right' }, minWidth: 0 }}
      />
    );
  }

  if (field === 'payday') {
    const num = typeof value === 'number' ? value : value ? parseInt(String(value), 10) : 0;
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        variant="standard"
        type="number"
        inputProps={{ min: 1, max: 31 }}
        value={num || ''}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          onSave(isNaN(v) ? 0 : Math.max(0, Math.min(31, v)));
        }}
        onBlur={(e) => {
          const v = parseInt((e.target as HTMLInputElement).value, 10);
          onSave(isNaN(v) ? 0 : Math.max(0, Math.min(31, v)));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const v = parseInt((e.target as HTMLInputElement).value, 10);
            onSave(isNaN(v) ? 0 : Math.max(0, Math.min(31, v)));
          }
          if (e.key === 'Escape') onCancel();
        }}
        InputProps={{ disableUnderline: false }}
        sx={{ '& input': { fontSize: 13 }, minWidth: 0 }}
      />
    );
  }

  if (field === 'dependents') {
    const num = typeof value === 'number' ? value : value ? parseInt(String(value), 10) : 0;
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        variant="standard"
        type="number"
        inputProps={{ min: 0 }}
        value={num ?? ''}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          onSave(isNaN(v) ? 0 : Math.max(0, v));
        }}
        onBlur={(e) => {
          const v = parseInt((e.target as HTMLInputElement).value, 10);
          onSave(isNaN(v) ? 0 : Math.max(0, v));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const v = parseInt((e.target as HTMLInputElement).value, 10);
            onSave(isNaN(v) ? 0 : Math.max(0, v));
          }
          if (e.key === 'Escape') onCancel();
        }}
        InputProps={{ disableUnderline: false }}
        sx={{ '& input': { fontSize: 13 }, minWidth: 0 }}
      />
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      fullWidth
      variant="standard"
      value={value != null ? String(value) : ''}
      onChange={(e) => onSave(e.target.value)}
      onBlur={(e) => onSave(e.target.value)}
      onKeyDown={(e) => handleKeyDown(e)}
      InputProps={{ disableUnderline: false }}
      sx={{ '& input': { fontSize: 13 }, minWidth: 0 }}
    />
  );
};
